import type { SavedProgression } from '@/types/progression';
import { getScaleDegreeColor } from '@/styles/colors';
import styles from './ProgressionCard.module.css';

interface ProgressionCardProps {
  progression: SavedProgression;
  onOpen: () => void;
  onDelete?: () => void;
}

/** Wrap the last word of a multi-word string in <i> */
function titleWithItalicSuffix(title: string): React.ReactNode {
  const parts = title.trim().split(/\s+/);
  if (parts.length < 2) return title;
  const last = parts.pop()!;
  return (
    <>
      {parts.join(' ')} <i>{last}</i>
    </>
  );
}

function relativeDate(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

/** Simple wc variant based on a short hash of the id */
function wcVariant(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) & 0xffffffff;
  }
  return (Math.abs(hash) % 4) + 1;
}

export const ProgressionCard: React.FC<ProgressionCardProps> = ({
  progression,
  onOpen,
  onDelete,
}) => {
  const chordCount = progression.chords?.length ?? 0;
  const totalBeats = progression.chords?.reduce((sum, c) => sum + (c.duration ?? 1), 0) || 1;
  const variant = wcVariant(progression.id);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  // Key color — use scale degree 1 (tonic) color for current mode as a proxy
  const keyDotColor = getScaleDegreeColor(1, progression.mode === 'minor' ? 'minor' : 'major');

  return (
    <div className={`${styles.card}`} onClick={onOpen}>
      {/* Preview tile */}
      <div className={`${styles.preview} ${styles[`wc${variant}`]}`}>
        {/* Horizontal center line */}
        <div className={styles.timelineRow} />

        {/* Chord glyphs */}
        {progression.chords?.map((chord, idx) => {
          const beatPos = progression.chords
            .slice(0, idx)
            .reduce((s, c) => s + (c.duration ?? 1), 0);
          const leftPct = (beatPos / totalBeats) * 80 + 8; // 8%–88% range
          const color = getScaleDegreeColor(chord.scaleDegree, chord.mode ?? progression.mode ?? 'major');
          return (
            <span
              key={chord.id ?? idx}
              className={styles.chordGlyph}
              style={{
                left: `${leftPct}%`,
                background: color,
              }}
            />
          );
        })}

        {onDelete && (
          <button
            className={styles.deleteButton}
            onClick={handleDelete}
            aria-label="Delete progression"
            title="Delete"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/>
            </svg>
          </button>
        )}
      </div>

      {/* Meta block */}
      <div className={styles.metaRow}>
        <h3 className={styles.title}>
          {titleWithItalicSuffix(progression.title)}
        </h3>
        <div className={styles.stats}>
          <span
            className={styles.keyDot}
            style={{ background: keyDotColor }}
          />
          <span>{progression.key.toUpperCase()} {progression.mode.toUpperCase()}</span>
          <span>&middot; {chordCount} chords</span>
          <span>&middot; edited {relativeDate(progression.updatedAt)}</span>
        </div>
        {progression.tags && progression.tags.length > 0 && (
          <div className={styles.tags}>
            {progression.tags.map((tag) => (
              <span key={tag} className={styles.tag}>{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
